import { api } from './api';
import { Patient, ApiResponse } from '../types';

interface CreatePatientData {
  name: string;
  species: string;
  breed: string;
  gender: 'MALE' | 'FEMALE';
  birthDate?: string;
  color?: string;
  microchipId?: string;
  ownerId: string;
}

interface UpdatePatientData extends Partial<CreatePatientData> {
  id: string;
}

interface PatientsQuery {
  page?: number;
  limit?: number;
  search?: string;
  species?: string;
  ownerId?: string;
  sortBy?: 'name' | 'createdAt' | 'birthDate';
  sortOrder?: 'asc' | 'desc';
}

class PatientService {
  // Get all patients with optional filtering
  async getPatients(query?: PatientsQuery): Promise<ApiResponse<{ patients: Patient[]; total: number; page: number; totalPages: number }>> {
    const params = new URLSearchParams();

    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const url = params.toString() ? `/patients?${params.toString()}` : '/patients';
    return api.get(url);
  }

  // Get patient by ID
  async getPatientById(id: string): Promise<ApiResponse<Patient>> {
    return api.get(`/patients/${id}`);
  }

  // Create new patient
  async createPatient(patientData: CreatePatientData): Promise<ApiResponse<Patient>> {
    return api.post('/patients', patientData);
  }

  // Update patient
  async updatePatient({ id, ...patientData }: UpdatePatientData): Promise<ApiResponse<Patient>> {
    return api.put(`/patients/${id}`, patientData);
  }

  // Delete patient
  async deletePatient(id: string): Promise<ApiResponse> {
    return api.delete(`/patients/${id}`);
  }

  // Upload patient photo
  async uploadPatientPhoto(id: string, file: File, onProgress?: (progress: number) => void): Promise<ApiResponse<{ photoUrl: string }>> {
    return api.upload(`/patients/${id}/photo`, file, onProgress);
  }

  // Get patient medical records
  async getPatientMedicalRecords(id: string): Promise<ApiResponse> {
    return api.get(`/patients/${id}/medical-records`);
  }

  // Get patient invoices
  async getPatientInvoices(id: string): Promise<ApiResponse> {
    return api.get(`/patients/${id}/invoices`);
  }

  // Search patients
  async searchPatients(searchTerm: string): Promise<ApiResponse<Patient[]>> {
    return api.get('/patients/search', { params: { q: searchTerm } });
  }

  // Get patients by owner
  async getPatientsByOwner(ownerId: string): Promise<ApiResponse<Patient[]>> {
    return api.get(`/patients/owner/${ownerId}`);
  }
}

export const patientService = new PatientService();